import { getTileType } from './game/terrainGenerator';
import { Player, PlayerType, Position } from './types';

interface RenderOptions {
  tileSize: number;
  gridColor: string;
  bgColor: string;
  playerColors: Record<PlayerType, string>;
  landColor: string;
  waterColor: string;
}

export class GameRenderer {
  private ctx: CanvasRenderingContext2D;
  private options: RenderOptions;
  private centerX: number;
  private centerY: number;
  private pellets: Position[] = [];

  constructor(ctx: CanvasRenderingContext2D, options: RenderOptions) {
    this.ctx = ctx;
    this.options = options;
    this.centerX = Math.floor(ctx.canvas.width / 2);
    this.centerY = Math.floor(ctx.canvas.height / 2);
  }

  private toCanvasCoordinates(position: Position): Position {
    return {
      x: position.x,
      y: -position.y, // Canvas Y grows down, game Y grows up
    };
  }

  public getCanvas(): HTMLCanvasElement {
    return this.ctx.canvas;
  }

  public getTileSize(): number {
    return this.options.tileSize;
  }

  public toCanvasSpace(
    pos: Position,
    worldCenter: Position,
    tileSize: number
  ): Position {
    return {
      x: (pos.x - worldCenter.x) * tileSize,
      y: -(pos.y - worldCenter.y) * tileSize, // Negate Y to fix orientation
    };
  }

  drawGame(players: Record<PlayerType, Player>, pellets: Position[]) {
    const canvas = this.ctx.canvas;
    const {
      tileSize,
      gridColor,
      bgColor,
      landColor,
      waterColor,
      playerColors,
    } = this.options;

    // Clear canvas
    this.ctx.fillStyle = bgColor;
    this.ctx.fillRect(0, 0, canvas.width, canvas.height);

    const centerX = Math.floor(canvas.width / 2);
    const centerY = Math.floor(canvas.height / 2);

    // Use topdown player position for world centering
    const worldCenter = players.topdown.position;

    // Calculate visible tile range
    const tilesX = Math.ceil(canvas.width / tileSize) + 2;
    const tilesY = Math.ceil(canvas.height / tileSize) + 2;
    const startX = Math.floor(-tilesX / 2);
    const startY = Math.floor(-tilesY / 2);

    // Draw map tiles
    for (let y = startY; y < startY + tilesY; y++) {
      for (let x = startX; x < startX + tilesX; x++) {
        const worldX = x + Math.floor(worldCenter.x);
        const worldY = y + Math.floor(worldCenter.y);
        const tileType = getTileType({ x: worldX, y: worldY });

        const canvasPos = this.toCanvasSpace(
          { x, y },
          { x: 0, y: 0 }, // No world center offset for tiles
          tileSize
        );

        // Draw tile
        this.ctx.fillStyle = tileType === 'land' ? landColor : waterColor;
        this.ctx.fillRect(
          centerX + canvasPos.x,
          centerY + canvasPos.y,
          tileSize,
          tileSize
        );

        // Draw grid lines
        this.ctx.strokeStyle = gridColor;
        this.ctx.strokeRect(
          centerX + canvasPos.x,
          centerY + canvasPos.y,
          tileSize,
          tileSize
        );
      }
    }

    // Draw pellets
    this.ctx.fillStyle = '#ff6b6b';
    pellets.forEach((pellet) => {
      const canvasPos = this.toCanvasSpace(pellet, worldCenter, tileSize);
      this.ctx.beginPath();
      this.ctx.arc(
        centerX + canvasPos.x,
        centerY + canvasPos.y,
        tileSize / 3,
        0,
        Math.PI * 2
      );
      this.ctx.fill();
    });

    // Draw players
    Object.entries(players).forEach(([type, player]) => {
      const playerType = type as PlayerType;
      const canvasPos = this.toCanvasSpace(
        player.position,
        worldCenter,
        tileSize
      );

      this.ctx.fillStyle = playerColors[playerType];
      this.ctx.beginPath();

      if (playerType === 'platformer') {
        this.ctx.fillRect(
          centerX + canvasPos.x - tileSize / 2,
          centerY + canvasPos.y - tileSize,
          tileSize,
          tileSize * 2
        );
      } else {
        this.ctx.arc(
          centerX + canvasPos.x,
          centerY + canvasPos.y,
          tileSize / 3,
          0,
          Math.PI * 2
        );
        this.ctx.fill();
      }
    });

    // Draw off-screen indicator for platformer
    if (players.platformer) {
      this.drawOffscreenIndicator(
        players.platformer.position,
        worldCenter,
        tileSize,
        playerColors.platformer
      );
    }
  }

  public isLandAt(position: Position): boolean {
    // Use the same terrain generator that's used for rendering
    return getTileType(position) === 'land';
  }

  private drawOffscreenIndicator(
    playerPos: Position,
    worldCenter: Position,
    tileSize: number,
    color: string
  ) {
    const { width, height } = this.ctx.canvas;
    const centerX = width / 2;
    const centerY = height / 2;
    const canvasPos = this.toCanvasSpace(playerPos, worldCenter, tileSize);

    // Calculate screen bounds with padding
    const padding = tileSize * 2;
    const bounds = {
      left: -centerX + padding,
      right: centerX - padding,
      top: -centerY + padding,
      bottom: centerY - padding,
    };

    // If player is off-screen
    if (
      canvasPos.x < bounds.left ||
      canvasPos.x > bounds.right ||
      canvasPos.y < bounds.top ||
      canvasPos.y > bounds.bottom
    ) {
      // Calculate angle to player
      const angle = Math.atan2(canvasPos.y, canvasPos.x);

      // Calculate indicator position on screen edge
      const radius = Math.min(centerX, centerY) - padding;
      const indicatorPos = {
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      };

      // Draw triangle indicator
      this.ctx.fillStyle = color;
      this.ctx.beginPath();
      this.ctx.save();
      this.ctx.translate(indicatorPos.x, indicatorPos.y);
      this.ctx.rotate(angle);
      this.ctx.moveTo(-10, -10);
      this.ctx.lineTo(10, 0);
      this.ctx.lineTo(-10, 10);
      this.ctx.closePath();
      this.ctx.fill();
      this.ctx.restore();
    }
  }

  public drawPellets(pellets: Position[], worldCenter: Position) {
    const {
      ctx,
      options: { tileSize },
    } = this;
    ctx.fillStyle = '#ff6b6b';

    pellets.forEach((pellet) => {
      const pos = this.toCanvasSpace(pellet, worldCenter, tileSize);
      ctx.beginPath();
      ctx.arc(
        this.centerX + pos.x,
        this.centerY + pos.y,
        tileSize / 4,
        0,
        Math.PI * 2
      );
      ctx.fill();
    });
  }
}
